import {toolbarSizes, defaultStyles} from "./defaults";
import { toolbarOptionMap } from "./structure";

export const styleFormats = {
	"font-weight": "bold",
	"font-style": "italic",
	"underline": "underline",
	"strike": "strike"
};

const needActionToId = {
	borders: true,
	align: true,
};

export function getToolbarText(text, action){
	if (needActionToId[action]) {
		text = `${action}-${text}`;
	}
	if (action === "format-row" || action === "format-column") {
		text = `format-${text.split("-")[0]}`;
	}
	const locale = webix.i18n.spreadsheet;
	return locale.tooltips[text] || locale.actions[text] || locale.labels[text] || text;
}

export function setToolbarLabels(options, action) {
	return options.map(o => {
		o.tooltip = getToolbarText(o.id, action);
		if (!o.value) o.value = o.tooltip; // check not to break actual non-localized values of selection controls
		if (o.submenu) o.submenu = setToolbarLabels(o.submenu, o.id);
		return o;
	});
}

export function formatTemplate(view, obj){
	//make it look like an actual cell
	var format =  view._formats.formatHelpers[obj.id] ? view._formats.formatHelpers[obj.id].getFormat : "";
	var cell = {css:""};
	var example = format && obj.example?format(obj.example, cell):"";
	return obj.value + (format?"<span class='webix_ssheet_right"+(cell.css?" "+cell.css:"")+"'>"+example+"</span>":"");
}

export const ui = {
	button: function(config){
		return {
			view: "ssheet-toggle", 
			width: config.width || toolbarSizes.width, 
			id: config.name, 
			name: config.name,  
			label: config.label,
			css: config.css||"", 
			onValue: styleFormats[config.name], 
			offValue: "normal",
			tooltip: getToolbarText(config.name)
		};
	},
	colorButton: function(config){
		return {
			view: "ssheet-color",  
			css: config.css, 
			name: config.name, 
			width: config.width || toolbarSizes.width + 24,
			title: "<span class='webix_ssheet_button_icon webix_ssheet_color_button_icon webix_ssheet_icon ssi-" + config.name + "' ></span>",
			tooltip: getToolbarText(config.name)
		};
	},
	toggleButton: function(config){
		return {
			view: "ssheet-toggle-silent", 
			width: toolbarSizes.width, 
			id: config.name, 
			name: config.name,
			label: "<span class='webix_ssheet_button_icon webix_ssheet_icon ssi-" + (config.icon || config.name) + "'></span>",
			tooltip: getToolbarText(config.name)
		};
	},
	iconButton: function(config){
		const btn = webix.copy(config);
		webix.extend(btn, {
			view: "button", 
			width: toolbarSizes.width, 
			id: config.name,
			label: "<span class='webix_ssheet_button_icon webix_ssheet_icon ssi-" + (config.icon || config.name) + "'></span>",
			css: "",
			tooltip: getToolbarText(config.name)
		});
		if (config.onValue) {
			webix.extend(btn, {
				view: "ssheet-toggle", 
				onValue: config.onValue, 
				offValue: config.offValue
			}, true);
		}

		return btn;
	},
	segmented: function(config){
		return  {
			view: "segmented", 
			name: config.name, 
			css: config.css || "", 
			width: config.width || toolbarSizes.width + 76,  
			options:config.options
		};
	},
	select: function(config){
		webix.extend(config, {
			view: "richselect",
			id: config.name,
			value: defaultStyles[config.name],
			suggest: {
				template: obj => obj.value,
				css: "webix_ssheet_suggest",
				padding: 0,
				data: setToolbarLabels(toolbarOptionMap[config.name])
			}
		});

		config.tooltip = getToolbarText(config.name);
		if (config.popupWidth){
			config.suggest.fitMaster = false;
			config.suggest.width = config.popupWidth;
		}
		if (config.popupTemplate)
			config.suggest.body = {
				template: config.popupTemplate
			};
		if (config.popupEvents){
			config.suggest.body = config.suggest.body || {};
			config.suggest.body.on = config.popupEvents;
		}
		return config;
	},
	separator: function(){
		return { view: "ssheet-separator" };
	},
	title: function(config){
		let title = config.title;
		if (title.indexOf("$") === 0)
			title = "";
		title = webix.i18n.spreadsheet.labels[config.title] || title;

		return {
			template: title, 
			view: "ssheet-bar-title", 
			height: toolbarSizes.titleHeight
		};
	},
	borders: function(config){
		return {
			view: "ssheet-borders", 
			width: config.width || toolbarSizes.width + 24, 
			data: setToolbarLabels(toolbarOptionMap[config.name], config.name), 
			id: config.name, 
			name: config.name,
			tooltip: getToolbarText(config.name)
		};
	},
	align: function(config){
		return {
			view: "ssheet-align", 
			value: defaultStyles[config.name], 
			width: config.width || toolbarSizes.width + 24, 
			data: setToolbarLabels(toolbarOptionMap[config.name], config.name),
			name: config.name, 
			tooltip: getToolbarText(config.name)
		};
	},
	condFormat: function(config){
		return {
			view: "ssheet-cond-format",
			width: 40,
			id: config.name,
			name: config.name
		};
	}
};

export const buttonsMap = {
	"font-family": function(){
		return ui.select({
			name: "font-family", 
			width: 100, 
			view:"combo"
		});
	},
	"font-size": function(){
		return  ui.select({
			name: "font-size", 
			width: 70, 
			view: "text", 
			icon: "wxi-menu-down", 
			css: "webix_ssheet_text",
			on: {
				onItemClick: function(){
					const suggest = webix.$$(this.config.suggest);
					if (!suggest.isVisible())
						suggest.show(this.getInputNode());
				}
			}
		});
	},
	"font-weight": function(){
		return ui.button({
			name:"font-weight",
			label: "B", 
			css: "webix_ssheet_bold"
		});
	},
	"font-style": function(){
		return ui.button({
			name: "font-style",
			label: "I",
			css:"webix_ssheet_italic"
		});
	},
	"underline": function(){
		return ui.button({
			name: "underline",
			label: "U",
			css: "webix_ssheet_underline"
		});
	},
	"strike": function(){
		return ui.button({
			name: "strike",
			label: "S",
			css: "webix_ssheet_strike"
		});
	},
	"color": function(){
		return ui.colorButton({
			name: "color",
			icon:"font",
			css:"webix_ssheet_color"
		});
	},
	"background": function(){
		return ui.colorButton({
			name: "background",
			icon: "paint-brush",
			css: "webix_ssheet_background", 
			width: 70
		});
	},
	"borders": function(){
		return ui.borders({ name: "borders" });
	},
	"text-align": function(){
		return ui.align({
			name: "text-align", 
			css: "webix_ssheet_align",
		});
	},
	"vertical-align": function(){
		return ui.align({
			name: "vertical-align", 
			css: "webix_ssheet_align",
		});
	},
	"wrap": function(){
		return ui.iconButton({
			name: "wrap", 
			onValue:"wrap", 
			offValue:"nowrap"
		});
	},
	"format": function(view){
		return ui.select({
			name: "format", 
			width: 106,
			popupWidth: 180,
			popupTemplate: obj => formatTemplate(view, obj),
			popupEvents:{
				onItemClick: function(id){
					if (id == "custom")
						this.getTopParentView().callEvent("onCommand", [{ id }]);
				}
			}
		});
	},
	"column": function(view){
		const options = setToolbarLabels(toolbarOptionMap["column"]);
		return {
			name: "column",
			view: "ssheet-button", 
			icon: "column",
			arrow: true, 
			width: 58,
			options: view.config.resizeCell ? options : options.filter(o => o.id !== "resize-column")
		};
	},
	"row": function(view){
		const options = setToolbarLabels(toolbarOptionMap["row"]);
		return {
			name: "row", 
			view: "ssheet-button", 
			icon: "row",
			arrow: true,
			width: 58,
			options: view.config.resizeCell ? options : options.filter(o => o.id !== "resize-row")
		};
	},
	"clear": function(){
		const options = setToolbarLabels(toolbarOptionMap["clear"]);
		return {
			name: "clear", 
			view: "ssheet-button", 
			icon: "clear-styles",
			arrow: true,
			width: 58,
			options
		};
	},
	"validation": function(){
		const options = setToolbarLabels(toolbarOptionMap["validation"]);
		return {
			name: "validation", 
			view: "ssheet-button", 
			icon: "validation",
			arrow: true,
			width: 58,
			options
		};
	},
	"image":function(){
		const options = setToolbarLabels(toolbarOptionMap["image"]);
		return {
			name: "image", 
			view: "ssheet-button", 
			icon: "add-image",
			arrow: true, 
			options
		};
	},
	"graph":function(){
		const options = setToolbarLabels(toolbarOptionMap["graph"]);
		return {
			name: "graph", 
			view: "ssheet-button", 
			icon: "add-sparkline",
			arrow: true,
			options
		};
	},
	"controls":function(){
		const options = setToolbarLabels(toolbarOptionMap["controls"]);
		return {
			name: "controls", 
			view: "ssheet-button", 
			icon: "add-control",
			arrow: true, 
			options
		};
	},
	"sort-range":function(){
		const options = setToolbarLabels(toolbarOptionMap["sort-range"]);
		return {
			name: "sort-range",
			view: "ssheet-button",
			icon: "sort-range",
			arrow: true,
			width: 58,
			options
		};
	},
	"sort-sheet":function(view){
		const options = setToolbarLabels(toolbarOptionMap["sort-sheet"]);
		return {
			name: "sort-sheet",
			view: "ssheet-button",
			icon: "sort-sheet",
			arrow: true,
			width: 58,
			options,
			ssView: view
		};
	},
	"comment": function(){
		return {
			name: "comment",
			view: "ssheet-button", 
			icon: "comments",
			arrow: true, 
			width: 55
		};
	},
	"create-filter": function(){
		return ui.toggleButton({ name: "create-filter" });
	},
	"hide-gridlines": function(){
		return ui.toggleButton({ name: "hide-gridlines" });
	},
	"hide-headers": function(){
		return ui.toggleButton({ name: "hide-headers" });
	},
	"show-formulas": function(){
		return ui.toggleButton({ name: "show-formulas" });
	},
	"print-borders": function(){
		return ui.toggleButton({ name: "print-borders", icon: "borders-no" });
	},
	"find-readonly": function(){
		return ui.iconButton({
			name: "find-readonly", 
			icon: "find"
		});
	},
};
